import type {
  BrandReferenceGenerationView,
  BrandReferenceRole,
  BrandReferenceSectionList,
} from "schemas/generated/brandReferences";
import type { RequestFn } from "./request";

export function createBrandReferencesClient({
  request,
}: {
  request: RequestFn;
}) {
  return {
    /** `generationId` selects a shelf site and routes through `/draft/sections`, the one listing that takes it. */
    listSections: ({
      projectId,
      generationId,
    }: {
      projectId: string;
      /** A shelf site other than the pointer's. */
      generationId?: string;
    }) => {
      const query = generationId
        ? `?generationId=${encodeURIComponent(generationId)}`
        : "";
      return request<BrandReferenceSectionList>({
        method: "GET",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references${generationId ? "/draft" : ""}/sections${query}`,
      });
    },
    list: ({ projectId }: { projectId: string }) => {
      return request<BrandReferenceGenerationView[]>({
        method: "GET",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references/draft/list`,
      });
    },
    discardDraft: ({
      projectId,
      generationId,
    }: {
      projectId: string;
      generationId: string;
    }) => {
      return request<{ removed: boolean }>({
        method: "DELETE",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references/draft/generations/${encodeURIComponent(generationId)}`,
      });
    },
    deleteDraftPage: ({
      projectId,
      pageId,
      generationId,
    }: {
      projectId: string;
      pageId: string;
      generationId?: string;
    }) => {
      return request<BrandReferenceSectionList>({
        method: "DELETE",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references/draft/pages/${encodeURIComponent(pageId)}${generationId ? `?generationId=${encodeURIComponent(generationId)}` : ""}`,
      });
    },
    deleteDraftSection: ({
      projectId,
      sectionId,
      generationId,
    }: {
      projectId: string;
      sectionId: string;
      generationId?: string;
    }) => {
      return request<BrandReferenceSectionList>({
        method: "DELETE",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references/draft/sections/${encodeURIComponent(sectionId)}${generationId ? `?generationId=${encodeURIComponent(generationId)}` : ""}`,
      });
    },
    get: ({ projectId }: { projectId: string }) => {
      return request<BrandReferenceGenerationView | null>({
        method: "GET",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references`,
      });
    },
    start: ({
      projectId,
      ...body
    }: {
      projectId: string;
      sourceUrl: string;
      role?: BrandReferenceRole;
    }) => {
      return request<BrandReferenceGenerationView>({
        method: "POST",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references/draft/start`,
        body,
      });
    },
    retry: ({
      projectId,
      ...body
    }: {
      projectId: string;
      generationId: string;
    }) => {
      return request<BrandReferenceGenerationView>({
        method: "POST",
        path: `/api/v1/projects/${encodeURIComponent(projectId)}/references/draft/retry`,
        body,
      });
    },
  };
}
